import React, { useState } from "react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconFilterOff,
} from "@tabler/icons-react";
import { useDailyReport } from "./context/DailyReportContext";
import { Card, Col, ListGroup, Row, Table } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
// Daftarkan komponen chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// 🔹 Generate tahun dan bulan secara dinamis
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Ambil 1 tahun ke belakang dan 1 tahun ke depan
  for (let y = currentYear - 1; y <= currentYear + 1; y++) {
    for (let m = 1; m <= 12; m++) {
      const value = `${y}-${String(m).padStart(2, "0")}`;
      const label = new Date(y, m - 1).toLocaleString("id-ID", {
        month: "long",
        year: "numeric",
      });
      options.push({ value, label });
    }
  }
  return options;
};


const DailyReport = () => {
  const { dailyReport, changeMonth, filterOptions } = useDailyReport();
  const [showFilters, setShowFilters] = useState(true);

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split("-").map(Number);
    changeMonth(year, month - 1);
  };

  const handlePrevMonth = () => {
    const current = filterOptions.filters.start_date ? new Date(filterOptions.filters.start_date) : new Date();
    const prev = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    changeMonth(prev.getFullYear(), prev.getMonth());
  };

  const handleNextMonth = () => {
    const current = filterOptions.filters.start_date ? new Date(filterOptions.filters.start_date) : new Date();
    const next = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    changeMonth(next.getFullYear(), next.getMonth());
  };


  const generateChartData = () => {
    if (!dailyReport) return { labels: [], datasets: [] };

    const dateLabels = Object.keys(dailyReport).sort();

    const datasetSingle = {
      label: "ATV Single",
      data: [],
      backgroundColor: "#206bc4",
      borderColor: "#1d5eb0",
      borderWidth: 1,
      borderRadius: 4,
    };

    const datasetTandem = {
      label: "ATV Tandem",
      data: [],
      backgroundColor: "#4299e1",
      borderColor: "#3b89cb",
      borderWidth: 1,
      borderRadius: 4,
    };

    const datasetExtra = {
      label: "Others",
      data: [],
      backgroundColor: "#f59e0b",
      borderColor: "#db8e0a",
      borderWidth: 1,
      borderRadius: 4,
    };


    dateLabels.forEach((date) => {
      const report = dailyReport[date];
      const activities = Array.isArray(report.activities)
        ? report.activities
        : Object.values(report.activities || {});

      let singleQty = 0;
      let tandemQty = 0;
      let extraQty = 0;

      activities.forEach((a) => {
        const name = a.activity;
        if (name === "ATV Single") {
          singleQty += a.qty;
        } else if (name === "ATV Tandem") {
          tandemQty += a.qty;
        } else {
          extraQty += a.qty;
        }
      });

      datasetSingle.data.push(singleQty);
      datasetTandem.data.push(tandemQty);
      datasetExtra.data.push(extraQty);
    });

    return {
      labels: dateLabels,
      datasets: [datasetSingle, datasetTandem, datasetExtra],
    };
  };

  const calculateSummary = () => {
    if (!dailyReport) return { totalPax: 0, totalActivities: 0 };
    let totalPax = 0;
    let totalActivities = 0;
    Object.values(dailyReport).forEach(data => {
      totalPax += data.total_pax || 0;
      const activities = Array.isArray(data.activities) ? data.activities : Object.values(data.activities || {});
      activities.forEach(a => totalActivities += a.qty || 0);
    });
    return { totalPax, totalActivities };
  };

  const summary = calculateSummary();

  return (
    <LayoutDashboard>
      <div className="container-fluid p-4">
        {/* 🔹 Header Section */}
        <div className="row align-items-center mb-4 g-3">
          <div className="col">
            <h1 className="m-0 fw-bold h2">Daily Report</h1>
            <p className="text-muted small m-0">
              Menampilkan data dengan status <span className="badge bg-success-lt text-success border-0">FINISHED</span>
            </p>
          </div>
          <div className="col-auto">
            <div className="btn-list">
              <div className="btn-group shadow-sm bg-white rounded">
                <button className="btn btn-icon btn-white border-0" onClick={handlePrevMonth}>
                  <IconChevronLeft size={20} />
                </button>
                <select
                  className="form-select border-0 shadow-none fw-bold text-primary px-3"
                  style={{ width: "auto", minWidth: "160px" }}
                  onChange={handleMonthChange}
                  value={filterOptions.filters.start_date?.slice(0, 7)}
                >
                  {getMonthOptions().map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <button className="btn btn-icon btn-white border-0" onClick={handleNextMonth}>
                  <IconChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 🔹 Summary Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-6 col-xl-3">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-primary-lt text-primary rounded-3 me-3">
                    <IconChevronRight size={24} />
                  </div>
                  <div>
                    <div className="text-muted small fw-bold text-uppercase ls-1">Total PAX</div>
                    <div className="h2 m-0 fw-bold">{summary.totalPax.toLocaleString("id-ID")}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-6 col-xl-3">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-info-lt text-info rounded-3 me-3">
                    <IconChevronLeft size={24} />
                  </div>
                  <div>
                    <div className="text-muted small fw-bold text-uppercase ls-1">Total Activities</div>
                    <div className="h2 m-0 fw-bold">{summary.totalActivities.toLocaleString("id-ID")}</div>
                  </div>
                </div>
              </Card.Body>

            </Card>
          </div>
        </div>

        {/* 🔹 Bar Chart Card */}
        <div className="row mb-4">
          <div className="col-12">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 pt-3 px-3">
                <h3 className="card-title fw-bold">Activity Trends</h3>
              </Card.Header>
              <Card.Body className="p-3">
                <div style={{ minHeight: "300px" }}>
                  <Bar
                    data={generateChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: "top", labels: { usePointStyle: true, boxWidth: 6, font: { weight: 'bold' } } },
                        tooltip: { mode: "index", intersect: false },
                      },
                      scales: {
                        x: { grid: { display: false } },
                        y: { beginAtZero: true, grid: { borderDash: [2] } },
                      },
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>


        {/* 🔹 Table Section */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="table-responsive">
            <table className="table table-vcenter table-hover card-table m-0">
              <thead className="bg-light">
                <tr>
                  <th className="w-1 py-3">#</th>
                  <th className="py-3">Date</th>
                  <th className="py-3">PAX</th>
                  <th className="py-3">Activities Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {dailyReport && Object.keys(dailyReport).length > 0 ? (
                  Object.entries(dailyReport).sort().map(([date, data], index) => (
                    <tr key={date}>
                      <td className="text-muted">{index + 1}</td>
                      <td className="fw-bold">{date}</td>
                      <td>
                        <span className="badge bg-primary-lt text-primary border-0 px-3">
                          {data.total_pax}
                        </span>
                      </td>
                      <td>
                        <ActivityList activities={data.activities} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted">
                      No data available for the selected month.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <style>{`
        .bg-primary-lt { background-color: rgba(32, 107, 196, 0.1) !important; }
        .bg-success-lt { background-color: rgba(47, 179, 68, 0.1) !important; }
        .bg-info-lt { background-color: rgba(66, 153, 225, 0.1) !important; }
        .ls-1 { letter-spacing: 0.05em; }
        .card-table thead th {
          text-transform: uppercase;
          font-size: 0.65rem;
          font-weight: 700;
          color: #64748b;
        }
      `}</style>
    </LayoutDashboard>

  );
};

export default DailyReport;

const ActivityListV1 = ({ activities }) => {
  const list = Array.isArray(activities)
    ? activities
    : Object.values(activities || {});

  return (
    <>
      {list.length === 0 && <div className="text-muted">No activity</div>}
      {list.map((a, i) => (
        <div key={i}>
          {a.activity} ({a.qty}) - Rp{" "}
          {parseInt(a.total_price).toLocaleString("id-ID")}
        </div>
      ))}
    </>
  );
};

const ActivityList = ({ activities }) => {
  const list = Array.isArray(activities)
    ? activities
    : Object.values(activities || {});

  if (list.length === 0) {
    return <span className="text-muted small italic">No activity</span>;
  }

  return (
    <div className="border rounded overflow-hidden shadow-xs">
      <table className="table table-sm table-borderless m-0" style={{ fontSize: "0.75rem" }}>
        <thead className="bg-light">
          <tr>
            <th className="py-1 px-2 border-bottom">Activity</th>
            <th className="py-1 px-2 border-bottom text-center">Qty</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a, i) => (
            <tr key={i} className={i !== list.length - 1 ? "border-bottom" : ""}>
              <td className="py-1 px-2 fw-medium">{a.activity}</td>
              <td className="py-1 px-2 text-center">
                <span className="badge bg-primary-lt text-primary fw-bold">
                  {a.qty}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};



const ActivityListV2 = ({ activities }) => {
  const list = Array.isArray(activities)
    ? activities
    : Object.values(activities || {});

  if (list.length === 0) {
    return <div className="text-muted text-center">No activity</div>;
  }

  return (
    <Row>
      {list.map((activity, index) => (
        <Col key={index} sm={12} md={4} lg={3}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>{activity.activity}</Card.Title>
              <Card.Text>
                <strong>Qty:</strong> {activity.qty}
              </Card.Text>
              <Card.Text>
                <strong>Price:</strong> Rp{" "}
                {parseInt(activity.total_price).toLocaleString("id-ID")}
              </Card.Text>
            </Card.Body>
            {/* sub total price */}
          </Card>
        </Col>
      ))}
    </Row>
  );
};
